import React from "react";
import { 
  Target, 
  CheckCircle,
  Star,
  Award
} from "lucide-react";

function AboutUs() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            About Yes Charge
          </h2>
          <div className="prose prose-lg text-gray-600 dark:text-gray-300 space-y-6">
            <p className="leading-relaxed">
              Yes Charge (YC) is an innovative digital service platform established under QUICKIE Top, 
              a UAE-based company specializing in telecom and digital product distribution. Yes Charge 
              has been established with a clear purpose to transform the digital payment and recharge 
              experience across the world through technology, transparency, and trust.
            </p>
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-700 transition-colors duration-200">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Star className="text-secondary-600 dark:text-secondary-400" size={24} />
            Our Core Business
          </h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
            Our core business focuses on delivering mobile top-ups, data and voice bundles, utility payments, 
            gaming credits, gift cards, and other digital products. In addition, we are expanding into e-commerce, 
            providing a marketplace for authentic Afghan products such as traditional clothing, dry fruits, natural 
            oils, and other locally sourced goods, connecting Afghan businesses to customers worldwide.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
            {[
              "Mobile Top-ups", "Data Bundles", "Voice Packages",
              "Utility Payments", "Gaming Credits", "Gift Cards",
              "Traditional Clothing", "Dry Fruits", "Natural Oils"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckCircle className="text-green-500 dark:text-green-400" size={16} />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Our Commitment
          </h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
            At Yes Charge, we are committed to providing fast, secure, and seamless transactions supported 
            by a robust technical infrastructure. Our system is designed to handle high transaction volumes 
            with accuracy, real-time monitoring, and full audit control. We continuously enhance our platform 
            to meet the latest industry standards in security, integration, and customer experience.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {[
              "Fast & seamless transactions",
              "High volume processing with accuracy",
              "Real-time monitoring",
              "Full audit control",
              "Latest security standards",
              "Continuous platform enhancement"
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl transition-colors duration-200">
                <div className="w-2 h-2 bg-secondary-600 dark:bg-secondary-400 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16 bg-secondary-50 dark:bg-secondary-900/20 rounded-3xl p-8 transition-colors duration-200">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Our Team
          </h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Yes Charge operates with a professional management and technical team that brings together 
            expertise from the telecom, IT, and financial technology sectors. Together, we aim to become 
            a trusted partner for mobile operators, service providers, and end users by simplifying access 
            to digital services.
          </p>
          
          <div className="flex items-center gap-4 mt-6 flex-wrap">
            {["Telecom", "IT", "FinTech"].map((sector, index) => (
              <span key={index} className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full text-sm font-medium text-secondary-700 dark:text-secondary-300 shadow-sm">
                {sector}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-primary-50 to-pink-50 dark:from-primary-900/20 dark:to-pink-900/20 rounded-2xl p-8 border border-primary-100 dark:border-primary-800 transition-colors duration-200">
            <div className="w-12 h-12 bg-primary-600 dark:bg-primary-500 rounded-xl flex items-center justify-center mb-4">
              <Target className="text-white" size={24} />
            </div>
            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Our Vision
            </h4>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              To become most trusted and innovative digital platform for telecom and financial services, 
              while serving as a secure global hub for Topup and authentic Afghan products and businesses.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-secondary-50 dark:from-blue-900/20 dark:to-secondary-900/20 rounded-2xl p-8 border border-blue-100 dark:border-blue-800 transition-colors duration-200">
            <div className="w-12 h-12 bg-blue-600 dark:bg-blue-500 rounded-xl flex items-center justify-center mb-4">
              <Award className="text-white" size={24} />
            </div>
            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Our Mission
            </h4>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              To empower digital access by providing reliable, fast, and secure delivery of telecom and 
              digital products, and to connect Afghans living abroad with their homeland through a seamless 
              and secure digital marketplace.
            </p>
          </div>
        </div>

        <div className="text-center bg-gradient-to-r from-secondary-600 to-primary-600 dark:from-secondary-700 dark:to-primary-700 rounded-2xl p-8 transition-colors duration-200">
          <p className="text-xl text-white font-medium">
            With Yes Charge, Say Yes to Faster and Secure Digital Hub.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AboutUs;